import React, { useMemo } from 'react'
import { css } from '@emotion/react'
import { Question } from '@/features/survey/survey-types'
import { useFormContext } from "react-hook-form";
import { useTranslation } from 'react-i18next'
import { useAppSelector } from '@/app/hooks'
import { ErrorMessage } from '@/components/atoms/error-message'

type Props = {
  question: Question
  index: number
}

export const SurveyScoreForm = ({ question, index }: Props) => {
  const { register, formState: { errors } } = useFormContext()
  const { t } = useTranslation()
  const design = useAppSelector(state => state.design)
  
  const brandColor = design?.theme.brandColor
  const cardColor = design?.background.cardColor
  const errorMessage = errors.answers?.[index]?.answerNumber?.message as string | undefined

  const dynamicStyles = useMemo(
    () => ({
      scoreOption: css({
        width: '100%',
        aspectRatio: '16 / 9',
        border: `1px solid ${brandColor}`,
        borderRadius: '6px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: cardColor,
        fontSize: 'clamp(16px, min(12cqw, 12cqh), 32px)',
        fontWeight: 'bold',
        color: brandColor,
        transition: 'all 0.2s ease',
        marginBottom: '8px'
      }),
      checked: css({
        'input[type="radio"]:checked + div': {
          backgroundColor: brandColor,
          color: cardColor,
        }
      }),
      edgeLabel: css({
        fontSize: '12px',
        color: brandColor,
      })
    }),
    [brandColor, cardColor],
  )

  return (
    <div css={wrapperStyle}>
      <div css={[gridStyle, dynamicStyles.checked]}>
        {[1, 2, 3, 4, 5].map((score) => (
          <label key={score} css={labelStyle}>
            <input 
              type="radio"
              {...register(`answers.${index}.answerNumber`, {
                required: question.isRequired ? t('This field is required') : false
              })} 
              value={score}
              css={inputStyle}
            />
            <div css={dynamicStyles.scoreOption}>
              {score}
            </div>
          </label>
        ))}
      </div>

      <div css={edgeLabelsRowStyle}>
        <span css={dynamicStyles.edgeLabel}>{t('Very dissatisfied')}</span>
        <span css={dynamicStyles.edgeLabel}>{t('Very satisfied')}</span>
      </div>
      
      {errorMessage && (
        <div css={errorMessageStyle}>
          <ErrorMessage text={errorMessage} />
        </div>
      )}
    </div>
  )
}

const gridStyle = css({
  display: 'grid',
  gridTemplateColumns: 'repeat(5, 1fr)',
  gap: 'clamp(8px, 2vw, 24px)',
  justifyItems: 'stretch',
  width: '100%',
})

const wrapperStyle = css({
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'stretch',
})

const labelStyle = css({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  cursor: 'pointer',
  position: 'relative',
  width: '100%',
  containerType: 'inline-size'
})

const inputStyle = css({
  position: 'absolute',
  opacity: 0,
  pointerEvents: 'none'
})

const edgeLabelsRowStyle = css({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  width: '100%'
})

const errorMessageStyle = css({
  marginTop: '0.5rem',
  marginBottom: '0.5rem'
})